import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import api from "../../../services/api";
import Loading from '../Loading'
import { dateAsNumbersWithoutSeconds } from '../../../utils/dateFormatter'

import TableFull from '../CommonTable/TableFull';

import { DiaryTrendsChart } from './DiaryTrendsChart'
import MultiSelectDropdown from './MultiSelectDropdown';

import * as CommonIcons from '../../CommonIcons';

import styles from './UserDiary.module.scss';


export default function UserDiary({ apiDiaryBaseUrl, pageDiaryBaseUrl }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [allData, setAllData] = useState([]);
  const [data, setData] = useState([]);
  const [selectedTrends, setSelectedTrends] = useState([]);
  const [trendParameters, setTrendParameters] = useState([]);
  const [chartData, setChartData] = useState({});

  const [canEditRecords] = useState(pageDiaryBaseUrl !== undefined);

  const columnArray =
    [{
      header: "No.",
      maxWidth: '0.5rem',
      enableColumnFilter: false,
      hideOnMobile: true,
      cell: ({ row }) => (
        <div>
          {row.index + 1}
        </div>
      )
    },
    {
      header: t('diary.table.header.date'),
      className: styles.dateCell,
      maxWidth: '3rem',
      accessorKey: "date",
      cell: info => {
        return (
          <>
            {dateAsNumbersWithoutSeconds(info.getValue())}
          </>
        )
      }

    },
    {
      header: t('diary.table.header.type'),
      accessorKey: "typeName",
    },
    {
      header: t('diary.table.header.value'),
      accessorKey: "value",
      allowWrap: true,
      cell: info => {
        const row = info.row.original;
        return (
          <span>
            {info.getValue()}
            {row.isAutoCalculated && (
              <i className={`fas fa-calculator ${styles.autoCalcIcon}`} title={t('diary.autoCalculated')} />
            )}
          </span>
        );
      }
    }
    ];

  if (canEditRecords)
    columnArray.push({
      header: '',
      accessorKey: "uid",
      hideOnMobile: true,
      cell: info => {
        const row = info.row.original;
        if (row.isAutoCalculated) return null;
        return (
          <div className={styles.editIcon}>
            <Link to={`${pageDiaryBaseUrl}/edit/${info.getValue()}`}><CommonIcons.PensilEdit /></Link>
          </div>
        )
      }
    });

  const columns = useMemo(
    () => columnArray,
    [t]
  );


  const loadRecords = () => {
    api
      .get(apiDiaryBaseUrl)
      .then((response) => {
        setAllData(response.data.diaryRecords);
        setData(response.data.diaryRecords);
        setTrendParameters(response.data.trendValues);
        setLoading(false);
      });
  };

  useEffect(() => {
    loadRecords();
  }, [apiDiaryBaseUrl]);


  if (loading)
    return <Loading />;


  const dropDownMenuActions = [{
    as: Link,
    getLinkTo: row => `${pageDiaryBaseUrl}/edit/${row.uid}`,
    text: t('common.editButton'),
    isVisible: row => !row.isAutoCalculated
  }];

  const onTrendSelectionChange = (selectedItems) => {    
    setSelectedTrends(selectedItems);
    
    if (selectedItems && selectedItems.length > 0) {
      // Filter table data to show only selected parameters
      const selectedUids = selectedItems.map(item => item.uid);
      setData(allData.filter(x => selectedUids.includes(x.typeUid)));
      
      // Prepare chart data for each selected parameter
      const newChartData = {};
      selectedItems.forEach(item => {
        let paramData = allData
          .filter(x => x.typeUid === item.uid)
          .map(x => [Date.parse(x.date), parseFloat(x.value)]);
        
        paramData.sort((a, b) => a[0] - b[0]);
        newChartData[item.uid] = {
          name: item.displayName,
          data: paramData
        };
      });
      
      setChartData(newChartData);
    } else {
      setData(allData);
      setChartData({});
    }
  };

  return (
    <>      
      <div className={`${styles.addNewBtnContainer}`}>
        {selectedTrends.length > 0 && 
          <Button className={`btn btn-primary ${styles.addNewBtn} me-3`} onClick={() => onTrendSelectionChange([])}>{t('diary.trends.clear')}</Button>
        }
        
        <MultiSelectDropdown
          items={trendParameters}
          selectedItems={selectedTrends}
          onSelectionChange={onTrendSelectionChange}
          placeholder={t('diary.trends.chooseParameter')}
          clearAllText={t('diary.trends.clear')}
          selectAllText={t('diary.trends.selectAll') || 'Select All'}
        />

      {pageDiaryBaseUrl && (
          <Link className={`btn btn-primary ${styles.addNewBtn}`} to={`${pageDiaryBaseUrl}/add`}>{t('diary.addNew')}</Link>
      )}
        </div>

      {selectedTrends.length > 0 &&
        <DiaryTrendsChart 
          selectedParameters={selectedTrends} 
          chartData={chartData} 
        />
      }

      <TableFull data={data}
        columns={columns}
        styles={styles}
        dropDownMenuActions={dropDownMenuActions}
        mainTableClassName={canEditRecords ? styles.table : styles.tableNoEdit}
      />     
    </>
  );
}
